import Decimal from "decimal.js";
import {useLocale} from "next-intl";
import {useCallback, useMemo} from "react";
import {useAppSelector} from "@/lib/hooks";
import {useGetBalanceQuery} from "@/lib/api/wallet/profileApi";
import {formatMoney, formatMoneyCompact, MoneyInput} from "@/lib/utils/money/formatMoney";

/**
 * AC-798: money formatting in the user's display currency.
 *
 * - `fm(amount)` formats an amount that is ALREADY in the display currency
 *   (e.g. values from the multi-currency /profile/balance array) — no
 *   conversion happens here.
 * - `fmUsd(amountUsd)` converts a USD-denominated amount (bonus terms,
 *   limits) with the backend-provided `rate` (units of display currency per
 *   1 USD, same rate the balance array was computed with) and formats it.
 *   Degrades to plain USD formatting when no rate is available (guest
 *   surfaces / stale cache) — never guesses a rate.
 *
 * Display only: every enforcement stays USD on the backend.
 */
export function useMoney() {
    const locale = useLocale();
    const userCurrency = useAppSelector(state => state.cashier.userCurrency);
    const currency = userCurrency || 'USD';
    const {data: balances} = useGetBalanceQuery(undefined, {skip: false});

    const rate = useMemo(() => {
        if (currency === 'USD') return 1;
        return balances?.find(b => b.currency === currency)?.rate;
    }, [balances, currency]);

    const fm = useCallback((amount: MoneyInput, currencyOverride?: string) =>
        formatMoney(amount, currencyOverride ?? currency, locale), [currency, locale]);

    const fmUsd = useCallback((amountUsd: MoneyInput) => {
        if (currency === 'USD' || rate == null) {
            return formatMoney(amountUsd, 'USD', locale);
        }
        return formatMoney(new Decimal(amountUsd || 0).mul(rate), currency, locale);
    }, [currency, locale, rate]);

    /**
     * AC-798: map-aware formatting for a USD-denominated amount that MAY have
     * a round per-currency override. `localValue` = the already-picked mapped
     * value for the display currency (formatted as-is, no conversion);
     * null/undefined = convert the USD base like fmUsd.
     */
    const fmMapped = useCallback((usdValue: MoneyInput, localValue?: number | null) =>
        localValue != null ? formatMoney(localValue, currency, locale) : fmUsd(usdValue),
        [currency, locale, fmUsd]);

    /** Same, but takes the whole per-currency map and picks the display currency itself. */
    const fmm = useCallback((usdValue: MoneyInput, byCurrency?: Record<string, number> | null) =>
        fmMapped(usdValue, byCurrency?.[currency]), [fmMapped, currency]);

    /**
     * AC-798: formats a USD amount AS USD, no conversion — for terms that
     * physically play out in USD regardless of the display currency (Free
     * Spins denominations and spin-win caps: FS packages are issued to the
     * provider in USD only).
     */
    const fmUsdOnly = useCallback((amountUsd: MoneyInput) =>
        formatMoney(amountUsd, 'USD', locale), [locale]);

    /**
     * Compact variant of fmUsd for tight card stat rows: weak-currency
     * conversions overflow fixed columns, so 10k+ renders as "ARS 14.9K".
     */
    const fmUsdC = useCallback((amountUsd: MoneyInput) => {
        if (currency === 'USD' || rate == null) {
            return formatMoneyCompact(amountUsd, 'USD', locale);
        }
        return formatMoneyCompact(new Decimal(amountUsd || 0).mul(rate), currency, locale);
    }, [currency, locale, rate]);

    return {currency, locale, rate, fm, fmUsd, fmUsdOnly, fmUsdC, fmMapped, fmm};
}
